import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/invoices")({
  component: InvoicesPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { RemoteSearchSelect } from "@/components/reusable-components/remote-search-select";
import { AccountSearchSelect } from "@/components/crm/shared/AccountSearchSelect";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchInvoices,
  useInvoiceMutations,
  type InvoiceOrdering,
} from "@/hooks/api/inventory/useInvoices";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import { fetchSalesOrders } from "@/hooks/api/inventory/useSales";
import { fetchDeliveryOrders } from "@/hooks/api/inventory/useDeliveryOrders";
import { inventoryKeys } from "@/hooks/api/inventory/keys";
import {
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { InvoiceTable } from "@/components/inventory/tables/InvoiceTable";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import { InvoiceFormSheet } from "@/components/inventory/forms/InvoiceFormSheet";
import { CashSaleFormSheet } from "@/components/inventory/forms/CashSaleFormSheet";
import { InvoiceDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import { Button } from "@/components/ui/button";
import { HandCoins } from "lucide-react";
import type {
  CashSalePayload,
  Invoice,
  InvoicePayload,
  InvoiceStatus,
} from "@/types/inventory";

const STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Sent", value: "SENT" },
  { label: "Paid", value: "PAID" },
  { label: "Overdue", value: "OVERDUE" },
  { label: "Void", value: "VOID" },
] satisfies { label: string; value: InvoiceStatus }[];

function InvoicesPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canVoidInvoices = can(capabilities, "inventory.finance.manage");
  const {
    createInvoice,
    createCashSale,
    updateInvoice,
    sendInvoice,
    markInvoicePaid,
    voidInvoice,
  } = useInvoiceMutations();
  const isMutating =
    createInvoice.isPending ||
    createCashSale.isPending ||
    updateInvoice.isPending ||
    sendInvoice.isPending ||
    markInvoicePaid.isPending ||
    voidInvoice.isPending;

  const [isCashSaleOpen, setIsCashSaleOpen] = useState(false);
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const catalogItemsQuery = useFetchItems(ALL_ACTIVE_PARAMS, {
    enabled: isCashSaleOpen,
  });
  const catalogItems = catalogItemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS, {
    enabled: isCashSaleOpen,
  });
  const units = unitsQuery.data?.results ?? [];

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | undefined>(
    undefined
  );
  const [accountFilter, setAccountFilter] = useState<string | undefined>(
    undefined
  );
  const [soFilter, setSoFilter] = useState<string | undefined>(undefined);
  const [doFilter, setDoFilter] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [showFilters, setShowFilters] = useState(false);
  const [viewTarget, setViewTarget] = useState<Invoice | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [sendTarget, setSendTarget] = useState<Invoice | null>(null);
  const [paidTarget, setPaidTarget] = useState<Invoice | null>(null);
  const [voidTarget, setVoidTarget] = useState<Invoice | null>(null);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        invoice_number: SortDirection;
        status: SortDirection;
        invoice_date: SortDirection;
        due_date: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      InvoiceOrdering
    >({
      invoice_number: null,
      status: null,
      invoice_date: null,
      due_date: null,
      created_at: null,
      updated_at: null,
    });

  const invoicesQuery = useFetchInvoices({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    status: statusFilter,
    account: accountFilter ? Number(accountFilter) : undefined,
    sales_order: soFilter ? Number(soFilter) : undefined,
    delivery_order: doFilter ? Number(doFilter) : undefined,
  });
  const invoices = invoicesQuery.data?.results ?? [];
  const count = invoicesQuery.data?.count ?? 0;

  const isLoading = invoicesQuery.isPending || isMutating;
  const isFetching = invoicesQuery.isFetching || isMutating;

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    ordering,
    statusFilter,
    accountFilter,
    soFilter,
    doFilter,
    setPage,
  ]);

  const resetFilters = () => {
    setOrdering({
      invoice_number: null,
      status: null,
      invoice_date: null,
      due_date: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setAccountFilter(undefined);
    setSoFilter(undefined);
    setDoFilter(undefined);
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
  };

  const handleOpenSheet = () => {
    setIsSheetOpen(true);
  };

  const handleOpenEditSheet = (inv: Invoice) => {
    setEditing(inv);
    setIsSheetOpen(true);
  };

  const handleCashSaleSubmit = async (payload: CashSalePayload) => {
    await createCashSale.mutateAsync(payload);
    toast.success("Cash sale completed");
    setIsCashSaleOpen(false);
  };

  const handleSubmit = async (payload: InvoicePayload) => {
    if (editing) {
      await updateInvoice.mutateAsync({ id: editing.id, payload });
      toast.success("Invoice updated");
    } else {
      await createInvoice.mutateAsync(payload);
      toast.success("Invoice created");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleSendConfirm = async () => {
    if (!sendTarget) return;
    try {
      await sendInvoice.mutateAsync({ id: sendTarget.id });
      toast.success(`${sendTarget.invoice_number} sent`);
      setSendTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handlePaidConfirm = async () => {
    if (!paidTarget) return;
    try {
      await markInvoicePaid.mutateAsync({ id: paidTarget.id });
      toast.success(`${paidTarget.invoice_number} marked paid`);
      setPaidTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleVoidConfirm = async () => {
    if (!voidTarget) return;
    try {
      await voidInvoice.mutateAsync({ id: voidTarget.id });
      toast.success(`${voidTarget.invoice_number} voided`);
      setVoidTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Invoices"
          description="Manage customer invoices"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleOpenSheet}
          addLabel="New Invoice"
          isLoading={isFetching}
        >
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => setIsCashSaleOpen(true)}
            disabled={isFetching}
          >
            <HandCoins className="h-4 w-4" />
            New Cash Sale
          </Button>
        </PageHeader>

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search invoice no..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
          <AccountSearchSelect
            className="w-fit"
            value={accountFilter ?? null}
            onChange={(v) => setAccountFilter(v == null ? undefined : String(v))}
            placeholder="Account"
          />
          <RemoteSearchSelect
            className="w-fit"
            queryKey={inventoryKeys.salesOrders.lists()}
            fetchPage={fetchSalesOrders}
            getId={(s) => s.id}
            getLabel={(s) => s.so_number}
            value={soFilter ? Number(soFilter) : null}
            onChange={(id) => setSoFilter(id == null ? undefined : String(id))}
            placeholder="Sales Order"
            searchPlaceholder="Search sales orders..."
            noItemsMessage="No sales orders found."
          />
          <RemoteSearchSelect
            className="w-fit"
            queryKey={inventoryKeys.deliveryOrders.lists()}
            fetchPage={fetchDeliveryOrders}
            getId={(d) => d.id}
            getLabel={(d) => d.do_number}
            value={doFilter ? Number(doFilter) : null}
            onChange={(id) => setDoFilter(id == null ? undefined : String(id))}
            placeholder="Delivery Order"
            searchPlaceholder="Search delivery orders..."
            noItemsMessage="No delivery orders found."
          />
        </FiltersPanel>

        <InvoiceTable
          invoices={invoices}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onNameClick={(inv) =>
            navigate({
              to: "/admin/inventory/invoices/$id",
              params: { id: String(inv.id) },
            })
          }
          onView={(inv) => setViewTarget(inv)}
          onEdit={handleOpenEditSheet}
          onSend={(inv) => setSendTarget(inv)}
          onMarkPaid={(inv) => setPaidTarget(inv)}
          onVoid={(inv) => setVoidTarget(inv)}
          canVoidInvoices={canVoidInvoices}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <InvoiceFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          isLoading={isMutating}
          editing={editing}
        />

        <CashSaleFormSheet
          isOpen={isCashSaleOpen}
          onClose={() => setIsCashSaleOpen(false)}
          onSubmit={handleCashSaleSubmit}
          warehouses={warehouses}
          items={catalogItems}
          units={units}
          isLoading={isMutating}
        />

        {/* Send confirm */}
        <DeleteConfirmDialog
          isOpen={!!sendTarget}
          onClose={() => setSendTarget(null)}
          onConfirm={handleSendConfirm}
          title="Send Invoice"
          description={`Send invoice ${sendTarget?.invoice_number} to the customer?`}
          itemName={sendTarget?.invoice_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Send Invoice"
          loadingLabel="Sending..."
        />

        {/* Mark paid confirm */}
        <DeleteConfirmDialog
          isOpen={!!paidTarget}
          onClose={() => setPaidTarget(null)}
          onConfirm={handlePaidConfirm}
          title="Mark Invoice Paid"
          description={`Mark invoice ${paidTarget?.invoice_number} as paid?`}
          itemName={paidTarget?.invoice_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Mark Paid"
          loadingLabel="Marking..."
        />

        {/* Void confirm */}
        <DeleteConfirmDialog
          isOpen={!!voidTarget}
          onClose={() => setVoidTarget(null)}
          onConfirm={handleVoidConfirm}
          title="Void Invoice"
          description={`Void invoice ${voidTarget?.invoice_number}? This cannot be undone.`}
          itemName={voidTarget?.invoice_number}
          isLoading={isMutating}
          destructive
          confirmLabel="Void Invoice"
          loadingLabel="Voiding..."
        />
      </div>

      <InvoiceDetailSheet
        isOpen={!!viewTarget}
        onClose={() => setViewTarget(null)}
        invoice={viewTarget}
      />
    </SidebarInset>
  );
}
